import { motion, type PanInfo, useMotionValue, animate } from 'framer-motion'
import * as React from 'react'
import { cn } from '#app/utils/misc.tsx'

export interface DraggableCarouselProps {
	/**
	 * Children elements to display in the carousel
	 */
	children: React.ReactNode
	/**
	 * Additional CSS classes for the container
	 */
	className?: string
	/**
	 * Gap between items in pixels (default: 24)
	 */
	gap?: number
	/**
	 * Padding on the sides in pixels (default: 16)
	 */
	padding?: number
	/**
	 * Momentum factor for drag end animation (default: 0.1)
	 */
	momentumFactor?: number
	/**
	 * Elastic factor for drag constraints (default: 0.2)
	 */
	elasticFactor?: number
	/**
	 * Spring animation configuration
	 */
	springConfig?: {
		damping?: number
		stiffness?: number
		mass?: number
	}
	/**
	 * Whether to show drag cursor (default: true)
	 */
	showCursor?: boolean
	/**
	 * Custom drag constraints
	 */
	dragConstraints?: {
		left?: number
		right?: number
	}
}

const DraggableCarousel = React.forwardRef<
	HTMLDivElement,
	DraggableCarouselProps
>(
	(
		{
			children,
			className,
			gap = 24,
			padding = 16,
			momentumFactor = 0.1,
			elasticFactor = 0.2,
			springConfig = {
				damping: 25,
				stiffness: 200,
				mass: 1,
			},
			showCursor = true,
			dragConstraints,
			...props
		},
		ref,
	) => {
		const containerRef = React.useRef<HTMLDivElement>(null)
		const [containerWidth, setContainerWidth] = React.useState(0)
		const [contentWidth, setContentWidth] = React.useState(0)

		// Motion values for smooth animations
		const x = useMotionValue(0)
		const [initialPosition, setInitialPosition] = React.useState(0)

		// Update container dimensions
		React.useEffect(() => {
			const updateDimensions = () => {
				if (containerRef.current) {
					const container = containerRef.current
					const content = container.querySelector(
						'[data-carousel-content]',
					) as HTMLElement

					if (container && content) {
						// Use getBoundingClientRect for more accurate measurements
						const containerRect = container.getBoundingClientRect()
						const contentRect = content.getBoundingClientRect()

						setContainerWidth(containerRect.width)
						setContentWidth(contentRect.width)

						// Calculate initial position - center content if it fits, otherwise start from left
						const needsScroll = contentRect.width > containerRect.width
						const newInitialPosition = needsScroll
							? 0
							: (containerRect.width - contentRect.width) / 2

						setInitialPosition(newInitialPosition)
						x.set(newInitialPosition)
					}
				}
			}

			// Use a small delay to ensure DOM is fully rendered
			const timeoutId = setTimeout(updateDimensions, 0)
			window.addEventListener('resize', updateDimensions)

			return () => {
				clearTimeout(timeoutId)
				window.removeEventListener('resize', updateDimensions)
			}
		}, [children, x])

		// Handle drag end with momentum
		const handleDragEnd = (event: any, info: PanInfo) => {
			const { velocity } = info

			// If content doesn't need scroll, always return to initial position
			if (!needsScroll) {
				animate(x, initialPosition, {
					type: 'spring',
					damping: springConfig.damping,
					stiffness: springConfig.stiffness,
					mass: springConfig.mass,
				})
				return
			}

			// Calculate target position based on velocity and current position
			const currentX = x.get()
			const targetX = currentX + velocity.x * momentumFactor

			// Apply constraints
			const maxX = dragConstraints?.right ?? 0
			const minX = dragConstraints?.left ?? -(contentWidth - containerWidth)
			const constrainedX = Math.max(minX, Math.min(maxX, targetX))

			// Animate to final position
			animate(x, constrainedX, {
				type: 'spring',
				damping: springConfig.damping,
				stiffness: springConfig.stiffness,
				mass: springConfig.mass,
			})
		}

		// Calculate default drag constraints based on initial position
		const needsScroll = contentWidth > containerWidth
		const defaultConstraints = needsScroll
			? {
					left: -(contentWidth - containerWidth),
					right: 0,
				}
			: {
					left: initialPosition,
					right: initialPosition,
				}

		const finalConstraints = dragConstraints ?? defaultConstraints

		return (
			<div
				ref={containerRef}
				className={cn('relative w-full overflow-hidden', className)}
				{...props}
			>
				{/* Carousel Content */}
				<motion.div
					data-carousel-content
					className={cn(
						'inline-flex items-start justify-start',
						showCursor && 'cursor-grab active:cursor-grabbing',
					)}
					style={{
						x,
						gap: `${gap}px`,
						paddingLeft: `${padding}px`,
						paddingRight: `${padding}px`,
						width: 'max-content',
					}}
					drag="x"
					dragConstraints={finalConstraints}
					dragElastic={elasticFactor}
					dragMomentum={true}
					onDragEnd={handleDragEnd}
					whileDrag={{ cursor: showCursor ? 'grabbing' : 'default' }}
				>
					{children}
				</motion.div>
			</div>
		)
	},
)

DraggableCarousel.displayName = 'DraggableCarousel'

export { DraggableCarousel }
